import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";
import AuthService from "services/AuthService";

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: localStorage.getItem(AUTH_TOKEN) || null,
  user: null,
  authLoaded: false,
};

export const signIn = createAsyncThunk(
  "login",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await AuthService.login({ email, password });
      const token = response.token;
      const refreshToken = response.refresh;
      
      localStorage.setItem(AUTH_TOKEN, token);
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN, refreshToken);
      }
      
      return { token, refreshToken };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const signOut = createAsyncThunk("api/v1/users/sign_out", async () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  return;
});

export const getUserInfo = createAsyncThunk(
  "me",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.getMe();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const refreshToken = createAsyncThunk(
  "refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
      if (!refreshTokenValue) {
        return rejectWithValue("Refresh token não encontrado");
      }
      
      const response = await AuthService.refreshToken(refreshTokenValue);
      const token = response.access;
      const newRefreshToken = response.refresh;
      
      localStorage.setItem(AUTH_TOKEN, token);
      if (newRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
      }
      
      return { token, refreshToken: newRefreshToken };
    } catch (err) {
      localStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(REFRESH_TOKEN);
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    hideLoading: (state) => {
      state.loading = false;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    authLoadedManually: (state) => {
      state.authLoaded = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload.token;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
        state.user = null;
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
        state.user = null;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.user = action.payload;
        state.authLoaded = true;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.user = null;
        state.message = action.payload;
        state.showMessage = true;
        state.authLoaded = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.token = null;
        state.user = null;
        state.redirect = "/";
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  hideLoading,
  signInSuccess,
  authLoadedManually,
} = authSlice.actions;

export default authSlice.reducer;
